import {
  AHText,
  Box,
  Circle,
  TouchableBox,
  useLayoutContext,
  useTheme,
  AHIcon,
} from "arkh";
import { Image } from "react-native";
import React, { Suspense } from "react";

import { BackgroundSelector } from "./WidgetNotePageBackgroundSelector";
import { TextControl } from "./TextControl";

import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import { graphql, useFragment, useMutation } from "react-relay";
import { PageSettingsLeft_page$key } from "./__generated__/PageSettingsLeft_page.graphql";
import { PageSettings_page$key } from "./__generated__/PageSettings_page.graphql";
import { PageSettingsSmallImage_image$key } from "./__generated__/PageSettingsSmallImage_image.graphql";
import { PageSettingsDeleteButton_image$key } from "./__generated__/PageSettingsDeleteButton_image.graphql";
import { useEvent } from "../../../../common/useEvent";
import { PageSettingsDeleteImageMutation } from "./__generated__/PageSettingsDeleteImageMutation.graphql";

type PageSettingsBaseProps = {
  widgetId: string;
  openCamera: () => void;
  openImagePicker: () => void;
  onRemovePage(): void;
};

PageSettings.heigth = 70;

export function PageSettingsLeft(
  props: PageSettingsBaseProps & {
    page: PageSettingsLeft_page$key;
  }
) {
  const theme = useTheme();
  const layout = useLayoutContext();

  const page = useFragment(
    graphql`
      fragment PageSettingsLeft_page on WidgetNotePage {
        id
        image {
          id
          ...PageSettingsDeleteButton_image
        }
        ...WidgetNotePageBackgroundSelector_widgetNotePage
        ...TextControl_widgetNotePage
      }
    `,
    props.page
  );
  return (
    <Box
      style={{
        flex: 1,
        flexDirection: "row",
        alignItems: "center",
        height: "100%",
      }}
      color="background"
      cornerRadius="medium"
    >
      <TouchableBox onPress={props.onRemovePage} spaceHorizontal="medium">
        <Circle
          color="status-negative"
          size="medium"
          style={{ alignItems: "center", justifyContent: "center" }}
        >
          <AHIcon type="file-minus" customColor={theme.surface} forceSize={14} />
        </Circle>
      </TouchableBox>
      <TextControl noteId={props.widgetId} page={page} />
      {page.image ? (
        <PageSettingsDeleteImageButton
          image={page.image}
          marginRight={layout.space.medium}
          pageId={page.id}
          noteId={props.widgetId}
        />
      ) : (
        <BackgroundSelector
          page={page}
          widgetId={props.widgetId}
          openImageSelection={props.openCamera}
          openImagePicker={props.openImagePicker}
        />
      )}
    </Box>
  );
}

export function PageSettings(
  props: PageSettingsBaseProps & {
    onDone: () => void;
    page: PageSettings_page$key;
  }
) {
  const theme = useTheme();
  const intl_tokens = useIntlTokens();
  const layout = useLayoutContext();

  const page = useFragment(
    graphql`
      fragment PageSettings_page on WidgetNotePage {
        id
        image {
          id
          ...PageSettingsDeleteButton_image
        }
        ...TextControl_widgetNotePage
        ...WidgetNotePageBackgroundSelector_widgetNotePage
      }
    `,
    props.page
  );

  return (
    <Box>
      <Box
        style={{
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          width: "100%",
        }}
      >
        <TouchableBox
          onPress={props.onRemovePage}
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            justifyContent: "center",
          }}
          spaceHorizontal="medium"
        >
          <Circle
            color="status-negative"
            size="medium"
            style={{ alignItems: "center", justifyContent: "center" }}
          >
            <AHIcon type="file-minus" customColor={theme.surface} forceSize={14} />
          </Circle>
        </TouchableBox>
        <Box
          style={{
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "row",
            marginHorizontal: layout.space.medium * 2 + layout.circle.medium,
          }}
          spaceLeft="medium"
        >
          <TextControl noteId={props.widgetId} page={page} />
          {page.image ? (
            <PageSettingsDeleteImageButton
              image={page.image}
              pageId={page.id}
              noteId={props.widgetId}
            />
          ) : (
            <BackgroundSelector
              page={page}
              widgetId={props.widgetId}
              openImageSelection={props.openCamera}
              openImagePicker={props.openImagePicker}
            />
          )}
        </Box>
        <TouchableBox
          style={{
            height: "100%",
            justifyContent: "center",
            position: "absolute",
            top: 0,
            bottom: 0,
            right: 0,
          }}
          spaceHorizontal="medium"
          onPress={props.onDone}
        >
          <AHText color="text-cta" type="title-small">
            {intl_tokens.Done}
          </AHText>
        </TouchableBox>
      </Box>
    </Box>
  );
}

function PageSettingsDeleteImageButton(props: {
  marginRight?: number;
  noteId: string;
  pageId: string;
  image: PageSettingsDeleteButton_image$key;
}) {
  const [removeImage] = useMutation<PageSettingsDeleteImageMutation>(graphql`
    mutation PageSettingsDeleteImageMutation($noteId: ID!, $pageId: ID!) {
      note(id: $noteId) {
        page(id: $pageId) {
          removeImage {
            id
            image {
              id
            }
          }
        }
      }
    }
  `);

  const theme = useTheme();
  const image = useFragment(
    graphql`
      fragment PageSettingsDeleteButton_image on Image {
        id
        ...PageSettingsSmallImage_image
      }
    `,
    props.image
  );

  const onRemoveImage = useEvent(() => {
    removeImage({
      variables: {
        noteId: props.noteId,
        pageId: props.pageId,
      },
      optimisticUpdater: (store) => {
        store.delete(image.id);
      },
    });
  });

  return (
    <TouchableBox
      onPress={onRemoveImage}
      style={{
        width: 30,
        height: 30,
        marginRight: props.marginRight,
      }}
    >
      <Suspense fallback={null}>
        <SmallImage image={image} />
      </Suspense>
      <Box
        cornerRadius="small"
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          height: 30,
          width: 30,
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(255,255,255,0.3)",
        }}
      >
        <AHIcon type="remove" customColor={theme.negative} forceSize={25} />
      </Box>
    </TouchableBox>
  );
}

function SmallImage(props: { image: PageSettingsSmallImage_image$key }) {
  const layout = useLayoutContext();
  const image = useFragment(
    graphql`
      fragment PageSettingsSmallImage_image on Image {
        id
        smallImage: size(minWidth: 100, minHeight: 100) {
          url
        }
      }
    `,
    props.image
  );

  return (
    <Image
      style={{
        width: 30,
        height: 30,
        borderRadius: layout.radius.small,
      }}
      progressiveRenderingEnabled
      resizeMode="cover"
      resizeMethod="scale"
      source={{ uri: image.smallImage?.url, cache: "force-cache" }}
    />
  );
}
